import { useMutation } from '@tanstack/react-query';
import {
  ActiveSourcingChannelDetails,
  SourcingService,
} from 'api-clients/monolith';
import { FormattedMessage, useIntl } from 'react-intl';

import ArrowUpIcon from '@/assets/icons/arrow-up.svg';
import { useConfirmation } from '@/components/base/confirmation';
import { useToaster } from '@/components/base/toaster';
import { BulbIcon } from '@/components/icons/bulb';
import { CloseIcon } from '@/components/icons/close';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { parseCurrency } from '@/utils/parse-currency';

import { useChannelsStore } from '../use-channels-store';

interface ChannelRecommendationProps {
  channel: ActiveSourcingChannelDetails;
}

export function ChannelRecommendation({ channel }: ChannelRecommendationProps) {
  const intl = useIntl();

  const { recommendation } = channel;
  const { notifySuccess, notifyError } = useToaster();
  const refetchOnCampaignUpdate = useChannelsStore(
    s => s.refetchOnCampaignUpdate,
  );
  const { confirm } = useConfirmation();

  const { mutate: handleAcceptRecommendation } = useMutation({
    mutationFn: () =>
      SourcingService.patchInternalApiSourcingSourcingPurchasesAcceptRecommendation(
        channel.sourcing_purchase_id!,
      ),
    onSuccess: async () => {
      await refetchOnCampaignUpdate();
      notifySuccess({
        message: intl.formatMessage({
          defaultMessage: 'Recommendation applied successfully!',
        }),
      });
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to update campaign!',
        }),
      });
    },
  });
  const { mutate: handleRejectRecommendation } = useMutation({
    mutationFn: () =>
      SourcingService.patchInternalApiSourcingSourcingPurchasesRejectRecommendation(
        channel.sourcing_purchase_id!,
      ),
    onSuccess: async () => {
      await refetchOnCampaignUpdate();
      notifySuccess({
        message: intl.formatMessage({
          defaultMessage: 'Recommendation cleared successfully!',
        }),
      });
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to clear recommendation!',
        }),
      });
    },
  });
  if (
    !channel.sourcing_purchase_id ||
    !recommendation ||
    !recommendation.budget
  )
    return null;

  const { budget } = recommendation;
  const isBudgetIncrease = budget > 0;

  const handleAcceptBudgetClick = async () => {
    const recommendedBudget =
      parseCurrency(channel.budget_stats?.expected) + budget;
    const confirmed = await confirm({
      title: intl.formatMessage({ defaultMessage: 'Confirm budget change' }),
      description: intl.formatMessage(
        {
          defaultMessage:
            'Are you sure you want to accept this budget? This will {margin} your campaign budget to {value}.',
        },
        {
          value: `$${recommendedBudget}`,
          margin: isBudgetIncrease ? 'increase' : 'decrease',
        },
      ),
      confirmLabel: intl.formatMessage(
        {
          defaultMessage: 'Yes, {margin} budget',
        },
        { margin: isBudgetIncrease ? 'increase' : 'decrease' },
      ),
    });

    if (confirmed) {
      handleAcceptRecommendation();
    }
  };

  return (
    <div className="bg-fx-blue-50 mx-5 mb-5 flex items-center justify-between gap-2 rounded-sm px-2 py-1.5">
      <div className="flex items-center gap-2">
        <BulbIcon className="text-fx-blue" />

        <div className="flex flex-col">
          <Typography variant="body2">
            <FormattedMessage
              defaultMessage="{margin} budget"
              values={{ margin: isBudgetIncrease ? 'Increase' : 'Decrease' }}
            />
          </Typography>
          {/* eslint-disable no-template-curly-in-string */}
          <Typography variant="subtitle1">
            {budget > 0 ? (
              <FormattedMessage
                defaultMessage="We recommend increasing this campaign budget by ${value}."
                values={{ value: Math.abs(budget) }}
              />
            ) : (
              <FormattedMessage
                defaultMessage="We recommend descreasing this campaign budget by ${value}."
                values={{ value: Math.abs(budget) }}
              />
            )}
          </Typography>
          {/* eslint-enable no-template-curly-in-string */}
        </div>
      </div>

      <div className="flex items-center justify-center gap-2">
        <Button
          variant="secondary"
          onClick={() => void handleAcceptBudgetClick()}
        >
          {isBudgetIncrease && <img src={ArrowUpIcon} alt="Edit" />}
          <FormattedMessage
            defaultMessage="{margin} budget"
            values={{ margin: isBudgetIncrease ? 'Increase' : 'Decrease' }}
          />
        </Button>
        <Button
          variant="icon"
          size="icon"
          onClick={() => void handleRejectRecommendation()}
          className="flex h-6 w-6 items-center justify-center"
        >
          <CloseIcon className="text-fx-primary hover:text-fx-primary" />
        </Button>
      </div>
    </div>
  );
}
